import { GetStaticPaths, GetStaticProps, GetStaticPropsContext } from "next";
import SiteChrome from "@chrome/SiteChrome";
import { HtmlPageTitle } from "@components/HtmlPageTitle";
import BlogPost, { sortBlogPosts } from "@components/blog/BlogPost";
import BlogTagPage from "@components/blog/BlogTagPage";
import BluesUniversityPage from "@components/blog/BluesUniversityPage";
import { MetaDescription } from "@components/MetaDescription";
import { getTagNameByValue } from "@content/en/blog/Tags";
import { SiteHtmlPageTitle } from "@content/en/HtmlTitle";
import { BlogHtmlPageTitle } from "@content/en/blog/HtmlTitle";
import { getAllBlogArticles, getAllBlogTags } from "@scripts/mdx";
import { stringToURI } from "@scripts/utils";

type BlogPageWithTagProps = {
  tagName: string;
  posts: BlogPost[];
};

const BlogTagLandingPage = ({ tagName, posts }: BlogPageWithTagProps) => {
  const isBluesUniversity = tagName === "Blues University";

  return (
    <>
      <SiteChrome noPadding={isBluesUniversity}>
        <HtmlPageTitle
          title={`${tagName} - ${BlogHtmlPageTitle} - ${SiteHtmlPageTitle}`}
        />
        <MetaDescription
          description={`Technical articles on ${tagName}. Written by developers, for developers.`}
        />

        {isBluesUniversity ? (
          <BluesUniversityPage posts={posts} />
        ) : (
          <BlogTagPage tagName={tagName} posts={posts} />
        )}
      </SiteChrome>
    </>
  );
};

export default BlogTagLandingPage;

export const getStaticPaths: GetStaticPaths = async () => {
  const should404ForSurprisePaths = true;

  const allTags = await getAllBlogTags();
  const allPaths = allTags.map((tag) => ({
    params: { tagName: stringToURI(tag) },
  }));

  return {
    paths: allPaths,
    fallback: !should404ForSurprisePaths,
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
}: GetStaticPropsContext) => {
  const articles = await getAllBlogArticles();

  let slug = "";
  if (params?.tagName) {
    slug = Array.isArray(params.tagName) ? params.tagName[0] : params.tagName;
  }

  const tagName = getTagNameByValue(slug);

  return {
    props: {
      tagName,
      posts: sortBlogPosts(
        articles.filter((post) => post.tags.includes(tagName)),
      ),
    },
  };
};
